import { ContentDto, SocialVersionDto } from '@app/modules/content-ai/content-ai.types';
import {
  DailymotionPlatform,
  DemoPlatform,
  FacebookPlatform,
  InstagramPlatform,
  socialPlatforms,
  TwitterPlatform,
  YoutubePlatform,
} from '@app/modules/social-export/domain/platforms';
import { useContentAiUpdateSocialVersionContent } from '@mi-front/api-v2';
import { Form, TextAreaField, TextField } from '@mi-front/ui';
import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { useContent } from '../../../../hooks/useContent';
import { ContentBadge, OutlinedButton, PrimaryButton } from '../../components';
import { ContentAiBodyFooterSelector } from '../user-article/AddFooter';
import { isSocialOperationInProgress } from './is-social-operation-in-progress';

export function SocialVersionUpdate({
  content,
  version,
  onSave,
}: {
  content: ContentDto;
  version: SocialVersionDto;
  onSave: () => void;
}) {
  const { $t } = useIntl();
  const queryClient = useQueryClient();
  const result = useContent(content.id);
  const mutation = useContentAiUpdateSocialVersionContent();
  const allowActions = !(result.data && isSocialOperationInProgress(result.data));

  const platform = socialPlatforms.get(version.platform);
  // Normalize nullable API fields (`string | null`) into Formik-friendly values.
  const sourceConfig = version.userConfig ?? version.generation.data;
  const initialValues = {
    title: sourceConfig?.title ?? undefined,
    body: sourceConfig?.body ?? undefined,
  };

  return (
    <Box>
      {/* Badge */}
      <Box sx={{ mb: 3 }}>
        <ContentBadge
          icon={platform.Icon({
            sx: { width: 16, height: 16, color: '#AF52DE' },
          })}
        >
          <FormattedMessage
            id="contentAi.socialVersion.generatedFor"
            defaultMessage="Contenido generado para {platform}"
            values={{ platform: platform.name }}
          />
        </ContentBadge>
      </Box>

      <Form
        initialValues={initialValues}
        onSubmit={async (data: { title?: string; body?: string }) => {
          mutation.mutate(
            {
              id: content.id,
              platform: version.platform,
              data: {
                title: data.title,
                body: data.body,
              },
            },
            {
              onSuccess: async () => {
                await queryClient.invalidateQueries({
                  queryKey: result.queryKey,
                });
                onSave();
              },
            },
          );
        }}
        actions={(fp) => (
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <PrimaryButton
              disabled={!allowActions}
              onClick={async () => fp.submitForm()}
              loading={mutation.isPending}
              sx={{
                fontWeight: 500,
                '&:hover': { backgroundColor: '#9C3FCC' },
              }}
            >
              <FormattedMessage id="common.form.saveBtnText" defaultMessage="Guardar" />
            </PrimaryButton>
            <OutlinedButton onClick={onSave} disabled={!allowActions}>
              <FormattedMessage id="common.cancel" defaultMessage="Cancelar" />
            </OutlinedButton>
          </Box>
        )}
      >
        {() => (
          <Box>
            {(version.platform === YoutubePlatform.id ||
              version.platform === FacebookPlatform.id) && (
              <>
                <TextField
                  name="title"
                  label={$t({
                    id: 'common.title',
                    defaultMessage: 'Título',
                  })}
                />
                <TextAreaField
                  label={$t({
                    id: 'socialExport.form.content',
                    defaultMessage: 'Contenido',
                  })}
                  name={`body`}
                  rows={8}
                  style={{
                    maxWidth: '900px',
                  }}
                  hint={<ContentAiBodyFooterSelector />}
                />
              </>
            )}
            {(version.platform === TwitterPlatform.id ||
              version.platform === DailymotionPlatform.id ||
              version.platform === InstagramPlatform.id ||
              version.platform === DemoPlatform.id) && (
              <TextAreaField
                label={$t({
                  id: 'socialExport.form.content',
                  defaultMessage: 'Contenido',
                })}
                name={`body`}
                rows={5}
                style={{
                  maxWidth: '900px',
                }}
              />
            )}
          </Box>
        )}
      </Form>
    </Box>
  );
}
